/**
 * The friends panel (design/v2.md §2.5): three tabs — Amigos, Solicitudes,
 * Añadir — rendered identically inside the side sheet and on the `/friends`
 * page, because the difference between those two is a container, not a screen.
 *
 * ── Where the data comes from ──────────────────────────────────────────────
 * Lists are fetched over REST and then *kept live by `SocialRoom` messages*, not
 * polled. So a write here does two things and no more: call the endpoint, then
 * re-read the lists it changed. The panel never patches its own copy optimistically —
 * every accepted write comes back to both parties as a message, and a second
 * writer would race the first.
 *
 * The invite button is the only control that talks to a different room: an
 * invitation is a `MusRoom` message, because only the table knows whether there
 * is a seat.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { Friend, FriendRequest, PresenceState, UserSearchResult } from '@ordago/shared';
import { USER_SEARCH_MIN_LENGTH } from '@ordago/shared';

import { friendsApi } from '../../net/friends';
import { ApiError } from '../../net/rest';
import { useStore } from '../../store';
import { invitableFriends } from '../../store/social';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { toast } from '../../ui/Toast';

import styles from './FriendsPanel.module.css';
import { PresenceDot } from './PresenceDot';

const PRESET_FALLBACK = '/avatars/presets/default-01.svg';

type Tab = 'friends' | 'requests' | 'add';

export interface FriendsPanelProps {
  /**
   * Sends `room.invite` on the table the viewer is sitting at, when there is
   * one. Absent everywhere else, which is what removes the Invitar buttons —
   * an affordance that cannot work should not be on screen.
   */
  onInvite?: (userId: string) => void;
  /** Told when a row is acted on, so the sheet can close behind an invite. */
  onInvited?: () => void;
}

export function FriendsPanel({ onInvite, onInvited }: FriendsPanelProps) {
  const { t } = useTranslation('social');
  const friends = useStore((s) => s.social.friends);
  const incoming = useStore((s) => s.social.incoming);
  const outgoing = useStore((s) => s.social.outgoing);
  const presence = useStore((s) => s.social.presence);
  const setFriends = useStore((s) => s.social.setFriends);
  const setRequests = useStore((s) => s.social.setRequests);

  const [tab, setTab] = useState<Tab>('friends');
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [list, requests] = await Promise.all([friendsApi.list(), friendsApi.requests()]);
    setFriends(list.friends);
    setRequests(requests.incoming, requests.outgoing);
  }, [setFriends, setRequests]);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const [list, requests] = await Promise.all([
          friendsApi.list(controller.signal),
          friendsApi.requests(controller.signal),
        ]);
        setFriends(list.friends);
        setRequests(requests.incoming, requests.outgoing);
      } catch {
        // The live channel is the primary source; a failed initial read leaves
        // the panel empty rather than broken, and the next message fills it.
      }
    })();
    return () => {
      controller.abort();
    };
  }, [setFriends, setRequests]);

  /** Runs a write, reports its refusal in the player's language, then re-reads. */
  const run = useCallback(
    async (key: string, action: () => Promise<unknown>, success?: string) => {
      setBusy(key);
      try {
        await action();
        if (success !== undefined) toast.success(success);
        await refresh();
      } catch (cause) {
        toast.error(t(errorKeyFor(cause)));
      } finally {
        setBusy(null);
      }
    },
    [refresh, t],
  );

  const onlineFriends = useMemo(
    () => friends.filter((f) => (presence[f.userId] ?? 'offline') !== 'offline').length,
    [friends, presence],
  );

  return (
    <div className={styles.panel} data-testid="friends-panel">
      <div className={styles.tabs} role="tablist" aria-label={t('title')}>
        {(['friends', 'requests', 'add'] as const).map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            className={styles.tab}
            data-active={tab === name}
            data-testid={`friends-tab-${name}`}
            onClick={() => {
              setTab(name);
            }}
          >
            {t(`tabs.${name}`)}
            {name === 'requests' && incoming.length > 0 ? (
              <span className={styles.tabCount} data-testid="friends-tab-count">
                {incoming.length}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === 'friends' && (
        <section className={styles.list} data-testid="friends-list">
          <p className={styles.subtle}>{t('counts.online', { count: onlineFriends })}</p>
          {friends.length === 0 ? (
            <p className={styles.empty}>{t('empty.friends')}</p>
          ) : (
            friends.map((friend) => (
              <FriendRow
                key={friend.userId}
                friend={friend}
                state={presence[friend.userId] ?? 'offline'}
                busy={busy === friend.userId}
                canInvite={
                  onInvite !== undefined && invitableFriends([friend], presence).length === 1
                }
                onInvite={() => {
                  onInvite?.(friend.userId);
                  toast.info(t('invite.sent', { name: friend.username }));
                  onInvited?.();
                }}
                onRemove={() => {
                  if (!window.confirm(t('confirm.remove', { name: friend.username }))) return;
                  void run(
                    friend.userId,
                    () => friendsApi.remove(friend.userId),
                    t('toast.removed'),
                  );
                }}
                onBlock={() => {
                  if (!window.confirm(t('confirm.block', { name: friend.username }))) return;
                  void run(
                    friend.userId,
                    () => friendsApi.block(friend.userId),
                    t('toast.blocked'),
                  );
                }}
              />
            ))
          )}
        </section>
      )}

      {tab === 'requests' && (
        <section className={styles.list} data-testid="friends-requests">
          <h3 className={styles.heading}>{t('sections.incoming')}</h3>
          {incoming.length === 0 ? (
            <p className={styles.empty}>{t('empty.incoming')}</p>
          ) : (
            incoming.map((request) => (
              <RequestRow key={request.id} request={request} busy={busy === request.id}>
                <Button
                  size="sm"
                  variant="primary"
                  data-testid={`friend-accept-${request.username}`}
                  onClick={() => {
                    void run(
                      request.id,
                      () => friendsApi.accept(request.id),
                      t('toast.accepted', { name: request.username }),
                    );
                  }}
                >
                  {t('actions.accept')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid={`friend-decline-${request.username}`}
                  onClick={() => {
                    void run(request.id, () => friendsApi.decline(request.id));
                  }}
                >
                  {t('actions.decline')}
                </Button>
              </RequestRow>
            ))
          )}

          <h3 className={styles.heading}>{t('sections.outgoing')}</h3>
          {outgoing.length === 0 ? (
            <p className={styles.empty}>{t('empty.outgoing')}</p>
          ) : (
            outgoing.map((request) => (
              <RequestRow key={request.id} request={request} busy={busy === request.id}>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid={`friend-cancel-${request.username}`}
                  onClick={() => {
                    void run(request.id, () => friendsApi.cancel(request.id));
                  }}
                >
                  {t('actions.cancel')}
                </Button>
              </RequestRow>
            ))
          )}
        </section>
      )}

      {tab === 'add' && <AddTab onAdded={refresh} />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────── rows

interface FriendRowProps {
  friend: Friend;
  state: PresenceState;
  busy: boolean;
  canInvite: boolean;
  onInvite: () => void;
  onRemove: () => void;
  onBlock: () => void;
}

function FriendRow({
  friend,
  state,
  busy,
  canInvite,
  onInvite,
  onRemove,
  onBlock,
}: FriendRowProps) {
  const { t } = useTranslation('social');

  return (
    <div className={styles.row} data-testid={`friend-row-${friend.username}`}>
      <img
        className={styles.avatar}
        src={friend.avatar.url64 === '' ? PRESET_FALLBACK : friend.avatar.url64}
        alt=""
        width={36}
        height={36}
      />
      <div className={styles.who}>
        <span className={styles.name}>{friend.username}</span>
        <span className={styles.state}>
          <PresenceDot state={state} />
          {t(`presence.${state}`)}
        </span>
      </div>
      <div className={styles.actions}>
        {canInvite && (
          <Button
            size="sm"
            variant="primary"
            disabled={busy}
            data-testid={`friend-invite-${friend.username}`}
            onClick={onInvite}
          >
            {t('actions.invite')}
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={busy} onClick={onRemove}>
          {t('actions.remove')}
        </Button>
        <Button size="sm" variant="danger" disabled={busy} onClick={onBlock}>
          {t('actions.block')}
        </Button>
      </div>
    </div>
  );
}

function RequestRow({
  request,
  busy,
  children,
}: {
  request: FriendRequest;
  busy: boolean;
  children: ReactNode;
}) {
  return (
    <div className={styles.row} data-testid={`request-row-${request.username}`} data-busy={busy}>
      <img
        className={styles.avatar}
        src={request.avatar.url64 === '' ? PRESET_FALLBACK : request.avatar.url64}
        alt=""
        width={36}
        height={36}
      />
      <div className={styles.who}>
        <span className={styles.name}>{request.username}</span>
      </div>
      <div className={styles.actions}>{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────── the add tab

function AddTab({ onAdded }: { onAdded: () => Promise<void> }) {
  const { t } = useTranslation('social');
  // A search result is a snapshot of the relationship at the moment it was
  // fetched, and the interesting case is exactly the one where it goes stale:
  // you ask, they accept while you are still looking at the row, and it would
  // otherwise keep saying "request sent" until you typed again. The live friend
  // list wins wherever it disagrees.
  const friends = useStore((s) => s.social.friends);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  /**
   * Debounced, and aborted on every keystroke. `userSearchByUser` allows thirty
   * a minute per account; a search-as-you-type with no debounce spends that in
   * about six seconds of ordinary typing.
   */
  useEffect(() => {
    const term = query.trim();
    if (term.length < USER_SEARCH_MIN_LENGTH) {
      setResults(null);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setSearching(true);
      void friendsApi
        .search(term, controller.signal)
        .then((res) => {
          setResults(res.users);
        })
        .catch(() => undefined)
        .finally(() => {
          setSearching(false);
        });
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  async function add(user: UserSearchResult): Promise<void> {
    setBusy(user.userId);
    try {
      await friendsApi.request(user.username);
      toast.success(t('toast.requestSent', { name: user.username }));
      setResults((current) =>
        (current ?? []).map((row) =>
          row.userId === user.userId ? { ...row, relation: 'requestSent' as const } : row,
        ),
      );
      await onAdded();
    } catch (cause) {
      toast.error(t(errorKeyFor(cause)));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className={styles.list} data-testid="friends-add">
      <Input
        label={t('search.label')}
        placeholder={t('search.placeholder')}
        value={query}
        data-testid="friend-search"
        onChange={(event) => {
          setQuery(event.target.value);
        }}
      />

      {results === null ? (
        <p className={styles.empty}>{t('empty.search')}</p>
      ) : results.length === 0 ? (
        <p className={styles.empty}>{searching ? '…' : t('empty.noResults')}</p>
      ) : (
        results.map((user) => {
          const relation = friends.some((f) => f.userId === user.userId)
            ? ('friends' as const)
            : user.relation;
          return (
            <div
              className={styles.row}
              key={user.userId}
              data-testid={`search-row-${user.username}`}
            >
              <img
                className={styles.avatar}
                src={user.avatar.url64 === '' ? PRESET_FALLBACK : user.avatar.url64}
                alt=""
                width={36}
                height={36}
              />
              <div className={styles.who}>
                <span className={styles.name}>{user.username}</span>
                {relation === null ? null : (
                  <Badge tone={relation === 'friends' ? 'success' : 'neutral'}>
                    {t(`relation.${relationKey(relation)}`)}
                  </Badge>
                )}
              </div>
              <div className={styles.actions}>
                {relation === null || relation === 'declined' ? (
                  <Button
                    size="sm"
                    variant="primary"
                    loading={busy === user.userId}
                    data-testid={`friend-add-${user.username}`}
                    onClick={() => {
                      void add(user);
                    }}
                  >
                    {t('actions.add')}
                  </Button>
                ) : null}
              </div>
            </div>
          );
        })
      )}
    </section>
  );
}

/**
 * `blocked` and `blockedBy` never reach here — search omits both sides of a
 * block — and `self` cannot, because the caller is excluded from its own
 * results. Both are folded onto `declined` so the union stays exhaustive
 * without inventing wording for a row that cannot be rendered.
 */
function relationKey(
  relation: NonNullable<UserSearchResult['relation']>,
): 'friends' | 'requestSent' | 'requestReceived' | 'declined' {
  switch (relation) {
    case 'friends':
    case 'requestSent':
    case 'requestReceived':
      return relation;
    case 'declined':
    case 'blocked':
    case 'blockedBy':
    case 'self':
      return 'declined';
  }
}

/**
 * The wording key for a refusal. Returned as a key rather than a string so the
 * typed `t()` keeps checking it: a code the locale has no line for is a compile
 * error here, not an untranslated string on somebody's screen.
 */
const FRIEND_ERROR_KEYS = [
  'errors.FRIEND_SELF',
  'errors.FRIEND_EXISTS',
  'errors.FRIEND_BLOCKED',
  'errors.FRIEND_LIMIT',
  'errors.FRIEND_REQUEST_COOLDOWN',
  'errors.FRIEND_NOT_FOUND',
] as const;

type FriendErrorKey = (typeof FRIEND_ERROR_KEYS)[number] | 'errors.generic';

function errorKeyFor(cause: unknown): FriendErrorKey {
  if (cause instanceof ApiError) {
    const key = `errors.${cause.code}`;
    const known = FRIEND_ERROR_KEYS.find((candidate) => candidate === key);
    if (known !== undefined) return known;
  }
  // Anything else — a network failure, a 500 — gets the generic line rather
  // than a friend-shaped explanation for a problem that is not about friends.
  return 'errors.generic';
}
